import { createAsyncThunk } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'

import { callErrorMsg } from 'helpers/errorMsg'
import type { RootStateType } from 'store'
import { getDocFileUrlAPI } from 'features/SCMS/services/configBank'
import {
  getSkuReviewSummary,
  getGoodValueOverview,
  getTrendOverview,
  getForecastProductList,
  getHubDistribution,
  exportForecast,
  getForecastProductListByHubId,
} from '../../services/demandPlanning'
import type {
  GoodValueOverviewParamsType,
  TrendOverviewParamsType,
  HubDistributionParamsType,
} from '../../@types/dpTypes'
import type { ForecastProductListParamsType } from '../../@types/forecastFilterTypes'

const sliceName = 'demandPlanning'

export const fetchSkuReviewSummary = createAsyncThunk(
  `${sliceName}/fetchSkuReviewSummary`,
  async ({ params }: { params?: ForecastProductListParamsType }, { rejectWithValue }) => {
    try {
      const response = await getSkuReviewSummary(params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchGoodValueOverview = createAsyncThunk(
  `${sliceName}/fetchGoodValueOverview`,
  async (params: GoodValueOverviewParamsType, { rejectWithValue }) => {
    try {
      const response = await getGoodValueOverview(params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchTrendOverview = createAsyncThunk(
  `${sliceName}/fetchTrendOverview`,
  async (params: TrendOverviewParamsType, { rejectWithValue }) => {
    try {
      const response = await getTrendOverview(params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchForecastProductList = createAsyncThunk(
  `${sliceName}/fetchForecastProductList`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        dpFilter: { appliedFilters },
      } = getState() as RootStateType
      if (appliedFilters?.hub_id) {
        const response = await getForecastProductListByHubId(appliedFilters ?? undefined)
        return response.data
      }
      const response = await getForecastProductList(appliedFilters ?? undefined)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchHubDistribution = createAsyncThunk(
  `${sliceName}/fetchHubDistribution`,
  async (
    {
      productId,
      params,
      successCallback,
    }: {
      productId: number
      params?: HubDistributionParamsType
      successCallback?: () => void
      append?: boolean
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getHubDistribution(productId, params)
      successCallback?.()
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const downloadForecastCsv = createAsyncThunk(
  `${sliceName}/downloadForecastCsv`,
  async ({ successCallback }: { successCallback: () => void }, { getState, rejectWithValue }) => {
    try {
      const {
        dpFilter: { appliedFilters },
      } = getState() as RootStateType
      const { data } = await exportForecast(appliedFilters)

      if (data?.file_url) {
        const downloadResponse = await getDocFileUrlAPI(data?.file_url, 'application/csv')
        saveAs(downloadResponse?.data, `scms_forecast_export.csv`)
      }

      successCallback()
      return data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)
